// All dimensions are in mm unless otherwise specified
// All font sizes are in pt unless otherwise specified

import { BarcodeExportConfigurationEntry } from '../../../types/barcode_export_configuration';
import { PdfDataKey } from './data/PdfDataKey';

export enum PdfComponentType {
  staticText = 'staticText',
  table = 'table',
  image = 'image',
  field = 'field',
  barcode = 'barcode',
}

export enum PdfTextAlign {
  left = 'left',
  center = 'center',
  right = 'right',
}

export enum PdfVerticalAlign {
  top = 'top',
  middle = 'middle',
  bottom = 'bottom',
}

export enum PdfBorderStyle {
  solid = 'solid',
  dashed = 'dashed',
  dotted = 'dotted',
}

export enum PdfFontFamily {
  helvetica = 'helvetica',
  helveticaBold = 'helveticaBold',
  timesRoman = 'timesRoman',
  timesBold = 'timesBold',
  courier = 'courier',
  courierBold = 'courierBold',
  symbol = 'symbol',
}

export enum PdfImageFit {
  contain = 'contain',
  cover = 'cover',
  fill = 'fill',
  autoScale = 'autoScale',
}

export enum PdfRepeat {
  always = 'always',
  first = 'first',
  last = 'last',
  attachment = 'attachment',
}

export enum PdfPageFormat {
  A4 = 'A4',
  A3 = 'A3',
}

export enum PdfOrientation {
  portrait = 'portrait',
  landscape = 'landscape',
}

export enum PdfTableType {
  productTransaction = 'productTransaction',
}

export enum PdfOverflow {
  none = 'none',
  wrap = 'wrap',
  clip = 'clip',
  ellipsis = 'ellipsis',
}

export interface PdfTemplateProperties {
  document: PdfTemplateDocumentProperties;
  components: PdfComponent[];
}

export interface PdfTemplateDocumentProperties {
  font: PdfComponentFontProperties;
  pageFormat: PdfPageFormat;
  orientation: PdfOrientation;
}

export interface PdfComponentFontProperties {
  size: number;
  family: PdfFontFamily;
  color: string;
}

export interface PdfComponentBorderProperties {
  enabled: boolean;
  width: number;
  style: PdfBorderStyle;
  color: string;
}

export interface BasePdfComponent {
  id: string;
  type: PdfComponentType;
  x: number;
  y: number;
  width: number;
  height: number;
  repeatMode: PdfRepeat;
  name?: string;
}

export type PdfComponent =
  | PdfStaticTextComponent
  | PdfFieldComponent
  | PdfTableComponent
  | PdfImageComponent
  | PdfBarcodeComponent;

export interface PdfStaticTextComponent extends BasePdfComponent {
  type: PdfComponentType.staticText;
  text: string;
  font?: PdfComponentFontProperties;
  border: PdfComponentBorderProperties;
  align: PdfTextAlign;
  valign: PdfVerticalAlign;
}

export interface PdfFieldComponent extends BasePdfComponent {
  type: PdfComponentType.field;
  key?: PdfDataKey<any>;
  font?: PdfComponentFontProperties;
  format?: string;
  border: PdfComponentBorderProperties;
  align: PdfTextAlign;
  valign: PdfVerticalAlign;
}

export interface PdfTableColumn {
  id: string;
  key?: PdfDataKey<any>;
  label: string;
  weight: number;
  width: number;
  align: PdfTextAlign;
  overflow: PdfOverflow;
}
export enum PdfSortDirection {
  ascending = 'ascending',
  descending = 'descending',
}

export enum PdfSortType {
  string = 'string',
  number = 'number',
}

export interface PdfTableSorting {
  key?: PdfDataKey<any>;
  direction: PdfSortDirection;
  sortType: PdfSortType;
}

interface PdfTableComponentSettings {
  productTransactions?: {
    completedOnly?: boolean;
  };
}

export interface PdfTableComponent extends BasePdfComponent {
  type: PdfComponentType.table;
  tableType: PdfTableType;
  columns: PdfTableColumn[];
  sorting: PdfTableSorting[];
  rowHeight: number;
  header: boolean;
  font?: PdfComponentFontProperties;
  headerFont?: PdfComponentFontProperties;
  groupKey?: PdfDataKey<any>;
  border: PdfComponentBorderProperties;
  borderCellsHorizontal: boolean;
  borderCellsVertical: boolean;
  settings?: PdfTableComponentSettings;
}

export interface PdfImageComponent extends BasePdfComponent {
  type: PdfComponentType.image;
  src?: string; // data URL
  key?: PdfDataKey<any>;
  fit: PdfImageFit;
  opacity: number;
  naturalWidthPx?: number;
  naturalHeightPx?: number;
  repeat: boolean;
}

export interface PdfBarcodeComponent extends BasePdfComponent {
  type: PdfComponentType.barcode;
  key: PdfDataKey<any>;
  entry: BarcodeExportConfigurationEntry;
}
